import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';

export default function About() {
  const values = [
    {
      icon: 'ri-heart-line',
      title: 'Customer First',
      description: 'Your satisfaction and safety are our top priorities in every journey we create',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Trust & Integrity',
      description: 'We build lasting relationships through honest communication and reliable service',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'Constantly evolving to bring you unique and memorable travel experiences',
    },
    {
      icon: 'ri-earth-line',
      title: 'Sustainability',
      description: 'Committed to responsible tourism that respects local communities and environments',
    },
  ];

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '500+', label: 'Happy Travelers' },
    { number: '100+', label: 'Destinations' },
    { number: '98%', label: 'Satisfaction Rate' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c4df8eef10c188bf8c45dd3ac9acaf44.jpg"
            alt="About Candy World Tours"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/40 to-navy-900/60"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            About Candy World Tours
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white/90 leading-relaxed"
          >
            Creating unforgettable journeys with care, adventure, and a touch of sweetness since 2014
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-accent-pink"></div>
                <span className="text-xs uppercase tracking-wider text-accent-pink font-semibold">
                  Our Story
                </span>
              </div>
              <h2 className="text-4xl font-bold text-navy-900 mb-6">
                A Journey That Started with Passion
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  <strong className="text-navy-900">Candy World Tours</strong> was born from a simple belief: travel should be sweet, memorable, and accessible to everyone. Founded in 2014 by a group of passionate travelers, we set out to create a travel company that combines professional service with genuine care for our clients.
                </p>
                <p>
                  What started as a small team organizing local tours has grown into a trusted travel partner serving hundreds of happy travelers each year. Our name reflects our philosophy - we believe every journey should be as delightful and satisfying as your favorite treat.
                </p>
                <p>
                  Today, we're proud to offer curated experiences across six continents, working with local partners who share our commitment to quality, authenticity, and sustainable tourism. Every trip we plan is infused with the same passion and attention to detail that inspired our founding.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20inviting%20travel%20agency%20office%20interior%20with%20world%20maps%20vintage%20suitcases%20and%20travel%20memorabilia%20creating%20cozy%20professional%20atmosphere%20representing%20travel%20company%20heritage%20and%20expertise&width=800&height=1000&seq=about2&orientation=portrait"
                  alt="Our Story"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-10 shadow-lg"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-accent-pink rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-navy-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To inspire and enable people to explore the world through carefully crafted travel experiences that are safe, enriching, and unforgettable. We strive to make travel accessible, enjoyable, and meaningful for every type of traveler, while supporting local communities and preserving the beauty of our destinations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl p-10 shadow-lg"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-accent-orange to-accent-yellow rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-navy-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To become the most trusted and beloved travel company, known for creating journeys that transform lives and connect cultures. We envision a world where travel brings people together, fosters understanding, and creates lasting positive impact on both travelers and the communities they visit.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-accent-orange"></div>
              <span className="text-xs uppercase tracking-wider text-accent-orange font-semibold">
                Our Values
              </span>
            </div>
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              What We Stand For
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do and every journey we create
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-accent-pink/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-4xl text-primary-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-primary-400 to-accent-pink bg-clip-text text-transparent mb-2">
                  {stat.number}
                </p>
                <p className="text-gray-400 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-navy-900 mb-6">
              Ready to Start Your Adventure?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Let us help you create memories that will last a lifetime
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="/tours"
                className="px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-pink text-white rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Explore Our Tours
              </a>
              <a
                href="#contact"
                className="px-8 py-4 border-2 border-navy-900 text-navy-900 rounded-full font-semibold hover:bg-navy-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}